import { request } from '../api';
import type { UxTraceFile } from './uxWatch';

export type UxMetricKey = 'screen_time' | 'clicks_per_segment' | 'repeated_path' | 'back_and_forth' | 'long_pause' | 'slow_request' | 'dead_click';

export const UX_METRIC_LABELS: Record<UxMetricKey, string> = {
  screen_time: 'время на экране',
  clicks_per_segment: 'нажатий на задачу',
  repeated_path: 'повтор пути',
  back_and_forth: 'туда-обратно',
  long_pause: 'пауза > 8 с',
  slow_request: 'запрос > 1,5 с',
  dead_click: 'мёртвое нажатие',
};

export interface UxSummary {
  durationMs: number;
  screens: number;
  clicks: number;
  segments: number;
  clicksPerSegment: number;
  repeatedPaths: number;
  backAndForth: number;
  longPauses: number;
  slowRequests: number;
  deadClicks: number;
  topScreens: { screen: string; ms: number }[];
}

export interface UxSuggestion {
  id: string;
  title: string;
  why: string;
  block: string | null;
  file: string | null;
  effort: 'S' | 'M' | 'L';
  evidence: { metric: UxMetricKey; actions: number[] }[];
  state: 'new' | 'accepted' | 'dismissed';
  reportId: number | null;
  /** Т15: the report's number in its project; absent on older ones (number = id). */
  reportNumber?: number | null;
  decidedBy: string | null;
}

export interface UxMetrics {
  screenTime: { screen: string; ms: number; visits: number }[];
  slowRequests: { method: string; url: string; ms: number; code: string | null }[];
  deadClicks: { target: string; block: string | null }[];
}

export interface UxSession {
  id: number;
  recorder: string | null;
  status: 'queued' | 'running' | 'done' | 'failed';
  startedAt: string | null;
  durationMs: number;
  events: number;
  summary: UxSummary | null;
  metrics: UxMetrics | null;
  suggestions: UxSuggestion[];
  model: string | null;
  error: string | null;
  createdAt: string;
}

export const uxApi = {
  save: (trace: UxTraceFile) =>
    request<{ session: { id: number; status: string; durationMs: number; events: number } }>('/ux-sessions', {
      method: 'POST',
      body: JSON.stringify({ trace }),
    }),
  list: () => request<{ sessions: UxSession[] }>('/ux-sessions'),
  get: (id: number) => request<{ session: UxSession }>(`/ux-sessions/${id}`),
  retry: (id: number) => request<{ session: UxSession }>(`/ux-sessions/${id}/retry`, { method: 'POST' }),
  decide: (id: number, sid: string, decision: 'accept' | 'dismiss') =>
    request<{ session: UxSession; reportId: number | null }>(`/ux-sessions/${id}/suggestions/${encodeURIComponent(sid)}`, {
      method: 'POST',
      body: JSON.stringify({ decision }),
    }),
  remove: (id: number) => request<{ ok: true }>(`/ux-sessions/${id}`, { method: 'DELETE' }),
};
